import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";
import _ from "lodash";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button, Typography } from "@mui/material";
import plusIcon from "@iconify/icons-mdi/plus";
import { InlineIcon } from "@iconify/react";
import { MyHrBackToPage, MyHrMassRoleDialog } from "my-hr/components";
import {
  sendGroupPasswordEmployeeList,
  sendGroupPasswordEmployeeListIsMobile,
} from "my-hr/configs/my-hr-table-defs";
import { MY_HR_SEND_GROUP_PASSWORDS } from "my-hr/graphql";
import { useMyHrFilteredList, useMyHrFilteredListCount } from "my-hr/hooks";
import {
  OcCard,
  OcConfirmationDialog,
  OcDataTable,
  OcFormSwitch,
  OcPageTransition,
  OcRoleFilter,
} from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcButtonGroup,
  useOcTableState,
  useOcIsMobile,
  useOcProperty,
} from "oc/hooks";
import { myHrRoleFilterState } from "states";

type Result = {
  errors?: any[];
  processed?: number;
  added?: number;
};

export const MyHRAdminSendGroupPasswordsPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const { data: loginType } = useOcProperty({
    resource: "system",
    key: "loginType",
  });

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  const buttonDefs = ["OC_ACTIVE", "OC_ALL"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "MyHRAdminGroupPasswordPageButtonGroup",
    buttonsDef: buttonDefs,
  });

  const [roleFilters, setRoleFilters] = useRecoilState(myHrRoleFilterState);
  const [replaceExistingPasswords, setRplaceExistingPasswords] =
    React.useState(false);
  const [disabled, setDisabled] = React.useState(false);
  const [sendGroupPasswords] = useMutation(MY_HR_SEND_GROUP_PASSWORDS, {
    onCompleted: (data) => {
      try {
        const parsed: Result =
          typeof data.sendGroupPasswords === "string"
            ? JSON.parse(data.sendGroupPasswords)
            : data.sendGroupPasswords;
        setResult(parsed);
        setDisabled(false);
      } catch {
        setResult({
          errors: [{ message: data.sendGroupPasswords }],
          processed: 0,
          added: 0,
        });
      }
    },
    onError: (error) => {
      console.log({ error });
      setDisabled(false);
    },
  });

  let tableId = "filteredListTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    pageSize: 5,
  });

  const resetRoleFilters = useResetRecoilState(myHrRoleFilterState);

  const handleCancel = () => {
    resetRoleFilters();
    history.push("/my-hr/users");
    setResult(null);
  };

  const handleAddFilter = ({ key, value }: { key: string; value: any }) => {
    const newState = _.cloneDeep(roleFilters);
    newState[key] = newState[key].concat([value]);
    setRoleFilters(newState);
  };

  const handleRemoveFilter = ({ key, value }: { key: string; value: any }) => {
    const newState = _.cloneDeep(roleFilters);
    newState[key] = newState[key].filter((item: any) => item !== value);
    setRoleFilters(newState);
  };

  const { data: filteredListCount } = useMyHrFilteredListCount({
    isActive: selectedButton === "OC_ACTIVE",
    roleFilters,
    useOrLogic: true,
  });

  const { data: filteredList, loading } = useMyHrFilteredList({
    page: tableData.page,
    pageSize: tableData.pageSize,
    isActive: selectedButton === "OC_ACTIVE",
    roleFilters,
    useOrLogic: true,
  });

  const handleSend = () => {
    setDisabled(true);
    const saveParams = {
      roleFilters,
      replaceExistingPasswords,
      locale: i18n.language,
    };
    sendGroupPasswords({ variables: saveParams });
  };

  const generateConfirmationText = () => {
    if (
      roleFilters.company.length === 0 &&
      roleFilters.orgUnit.length === 0 &&
      roleFilters.workingPlace.length === 0 &&
      roleFilters.jobClassGroup.length === 0 &&
      roleFilters.payOffice.length === 0 &&
      roleFilters.contract.length === 0
    ) {
      return "MY_HR_SEND_GROUP_PASSWORDS_TO_ALL_DIALOG_TEXT";
    }
    return "MY_HR_SEND_GROUP_PASSWORDS_DIALOG_TEXT";
  };

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const handleOKConfirmationDialog = () => {
    setShowConfirmationDialog(false);
    setShowDialog(true);
    handleSend();
  };

  const handleCancelConfirmationDialog = () => {
    setShowConfirmationDialog(false);
  };

  const [showDialog, setShowDialog] = React.useState(false);
  const [result, setResult] = useState<Result | null>(null);

  const handleShowDialog = () => {
    setShowDialog((prev) => !prev);
  };

  const handleFinish = () => {
    setShowDialog(false);
    resetRoleFilters();
    setResult(null);
  };

  const handleCloseImportDialog = () => {
    setShowDialog(false);
  };

  const tableDef = isMobile
    ? sendGroupPasswordEmployeeListIsMobile
    : sendGroupPasswordEmployeeList;

  const filteredTableDef = {
    id: sendGroupPasswordEmployeeList?.id,
    columns: sendGroupPasswordEmployeeList?.columns?.filter(
      (column) =>
        !sendGroupPasswordEmployeeListIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const onSelectRow = (o: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? null : o?._id);
    }
  };

  if (loginType === "sso") {
    return (
      <OcPageTransition>
        <Box padding={"16px"}>
          <MyHrBackToPage
            label={t("MY_HR_ADD_GROUP_PASSWORD_TITLE")}
            onBack={handleCancel}
            isMobile={isMobile}
          />
          <OcCard label={t("MY_HR_ADD_GROUP_PASSWORD_TITLE")} loading={false}>
            <>
              <Box>
                <Typography variant="body1">
                  {t("MY_HR_ADD_GROUP_PASSWORD_NOT_ON_SSO")}
                </Typography>
              </Box>
            </>
          </OcCard>
        </Box>
      </OcPageTransition>
    );
  }
  console.log(result)
  return (
    <OcPageTransition>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="100%"
      >
        <OcConfirmationDialog
          showDialog={showConfirmationDialog}
          title={t("MY_HR_SEND_GROUP_PASSWORDS_DIALOG_TITLE")}
          texts={[t(generateConfirmationText())]}
          okButtonText={t("OC_SAVE")}
          cancelButtonText={t("OC_CANCEL")}
          onClose={handleOKConfirmationDialog}
          onCancel={handleCancelConfirmationDialog}
        />
        <MyHrMassRoleDialog
          onShowDialog={handleShowDialog}
          showDialog={showDialog}
          onFinish={handleFinish}
          onCloseDialog={handleCloseImportDialog}
          result={result}
          infoText={t("MY_HR_SEND_GROUP_PASSWORDS_INFO")}
          successText={t("MY_HR_SEND_GROUP_PASSWORDS_SUCCEEDED")}
          successInfo={t("MY_HR_PASSWORD_SENT")}
          toolbarLabel={t("MY_HR_ADD_GROUP_PASSWORD_TITLE")}
          isMobile={isMobile}
          errorText={t("MY_HR_SEND_GROUP_PASSWORDS_FAILED")}
          action={t("MY_HR_NEW_PASSWORD")}
        />

        <Box padding={isMobile ? "16px 0px" : "16px"}>
          <MyHrBackToPage
            label={t("MY_HR_ADD_GROUP_PASSWORD_TITLE")}
            onBack={handleCancel}
            isMobile={isMobile}
          />
          <Box height="16px" />
          <OcCard label={t("MY_HR_USERS")} loading={disabled}>
            <>
              <OcRoleFilter
                subtypeProps={{
                  queryType: "admin",
                  handleAddItem: handleAddFilter,
                  handleRemoveItem: handleRemoveFilter,
                  roleFilters,
                  product: "MyHR",
                  role: "admin",
                }}
                OcButtonGroup={OcButtonGroup}
                summaryItems={[
                  {
                    label: "OC_COUNT_OF_EMPLOYEES",
                    value: filteredListCount,
                    color: "primary",
                  },
                ]}
                buttonBarProps={{
                  handleRightButton: resetRoleFilters,
                  primaryButtonText: "",
                  noLine: true,
                  paddingTop: "10px",
                }}
              />
              <Box paddingTop="32px">
                <OcDataTable
                  data={filteredList}
                  loading={loading && !tableData}
                  tableDef={tableDef}
                  tableData={tableData}
                  setTableData={setTableData}
                  pagination={true}
                  onSelectRow={onSelectRow}
                  filteredTableDef={filteredTableDef}
                  selectedRowId={selectedRowId}
                />
              </Box>
              {isMobile && <Box height="16px" />}
              <Box display="flex" flexDirection="row" alignItems="center">
                <OcFormSwitch
                  id="replaceExistingPasswords"
                  checked={replaceExistingPasswords}
                  inputProps={{ "aria-label": "controlled" }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setRplaceExistingPasswords(e.target.checked)
                  }
                  disabled={disabled}
                />
                <Box width="16px" />
                <Typography variant="body1">
                  {t("MY_HR_PASSWORD_SWITCH_TEXT")}
                </Typography>
              </Box>
              <Box paddingTop="32px">
                <Typography variant="body1">
                  {t("MY_HR_SEND_PASSWORD_INFO_TEXT")}
                </Typography>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                paddingTop="24px"
              >
                <Button
                  id="new-password-button"
                  variant="contained"
                  color="primary"
                  disableElevation
                  startIcon={<InlineIcon icon={plusIcon} />}
                  disabled={disabled}
                  onClick={() => setShowConfirmationDialog(true)}
                >
                  {t("MY_HR_NEW_PASSWORD")}
                </Button>
              </Box>
            </>
          </OcCard>
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyHRAdminSendGroupPasswordsPage;
